/**
 * Pure mission-selection function. Exposed separately so it can be unit-tested
 * without a DOM or React.
 */
import type { AtlasContext, AtlasMission, AtlasMissionId, AtlasMissionState } from './atlas.types';
import { ATLAS_MISSIONS, getMissionById } from './missions';

export interface MissionWithState {
  mission: AtlasMission;
  state: AtlasMissionState;
}

/**
 * Optional permission predicate used to gate Atlas missions whose
 * `requiredPermission` field is set. Centralised here so both
 * `chooseCurrentMission` and `dockableMissions` filter identically.
 *
 * Passing `undefined` (the default) treats every permission as granted —
 * keeps backward compatibility with the previous signature.
 */
export type PermissionPredicate = (key: string) => boolean;

function hasMissionPermission(
  mission: AtlasMission,
  permissionPredicate?: PermissionPredicate
): boolean {
  if (!mission.requiredPermission) return true;
  if (!permissionPredicate) return true;
  return permissionPredicate(mission.requiredPermission);
}

/** Returns true when the mission is actively snoozed right now. */
export function isSnoozed(state: AtlasMissionState, now: number = Date.now()): boolean {
  return state.snoozedUntil != null && state.snoozedUntil > now;
}

/**
 * Priority-ordered mission selection. This never returns a mission that is
 * completed, skipped, or currently snoozed. Context guards keep certain
 * missions out of the way when prerequisites aren't met. When
 * `permissionPredicate` is supplied, missions whose `requiredPermission` is
 * not granted are also filtered out — used to keep the Reports tour off
 * Member dashboards.
 */
export function chooseCurrentMission(
  ctx: AtlasContext,
  statesByMission: Record<AtlasMissionId, AtlasMissionState>,
  now: number = Date.now(),
  permissionPredicate?: PermissionPredicate
): AtlasMissionId | null {
  function available(id: AtlasMissionId, guard: boolean = true): boolean {
    if (!guard) return false;
    const s = statesByMission[id];
    if (!s) return false;
    if (s.completed || s.skipped) return false;
    if (isSnoozed(s, now)) return false;
    const mission = getMissionById(id);
    if (mission && !hasMissionPermission(mission, permissionPredicate)) return false;
    return true;
  }

  if (!ctx.welcomeCompleted && available('welcome')) return 'welcome';
  // Derived blocking steps come first: without an org or a product the rest
  // of the app literally has nothing to show. The dock launcher glows while
  // either is open (see `critical` flag).
  if (available('create-organization', !ctx.hasOrg)) return 'create-organization';
  if (available('add-first-product', ctx.hasOrg && !ctx.hasProduct)) return 'add-first-product';
  if (available('wire-integrations', ctx.hasProduct && !ctx.hasJira)) return 'wire-integrations';
  if (available('load-analytics-data', ctx.hasJira && !ctx.hasAnalyticsData))
    return 'load-analytics-data';
  if (available('post-full-update', ctx.postUpdateJourneyActive)) return 'post-full-update';
  if (available('calendar-connect', ctx.hasJira && !ctx.hasSharePoint)) return 'calendar-connect';
  // `first-report` (Restored in Iter 7): owner/admin only — Members get
  // filtered out by `requiredPermission: 'reports.access'`.
  if (available('first-report', ctx.hasAnalyticsData)) return 'first-report';
  if (available('first-baseline', ctx.hasAnalyticsData)) return 'first-baseline';
  // Tours over already-good pages: surface last, only if not completed.
  if (available('releases-tour', ctx.hasAnalyticsData)) return 'releases-tour';
  if (available('forecast-tour', ctx.hasAnalyticsData)) return 'forecast-tour';
  return null;
}

/**
 * Flattens missions + states for the Dock list (excludes `welcome` because
 * that is the modal, not a checklist item). When `permissionPredicate` is
 * supplied, permission-gated missions the user can't reach are dropped from
 * the dock — keeps the checklist tidy for members.
 */
export function dockableMissions(
  statesByMission: Record<AtlasMissionId, AtlasMissionState>,
  permissionPredicate?: PermissionPredicate
): MissionWithState[] {
  return ATLAS_MISSIONS.filter(
    (m) => m.id !== 'welcome' && hasMissionPermission(m, permissionPredicate)
  ).map((m) => ({
    mission: m,
    state: statesByMission[m.id],
  }));
}
